export type CapabilityLevel = 0 | 1 | 2;

export interface CapabilitySnapshot {
  level: CapabilityLevel;
  label: string;
  reasons: string[];
  online: boolean;
  hasWebSpeech: boolean;
  hasGemini: boolean;
}

export interface CapabilityOptions {
  online?: boolean;
  webSpeechAvailable?: boolean;
  geminiKey?: string | null;
  geminiEnabled?: boolean;
  forceLevel?: CapabilityLevel;
}

const labels: Record<CapabilityLevel, string> = {
  0: '基础模式（仅录音回放）',
  1: '浏览器 STT 模式',
  2: 'Gemini 增强模式',
};

/**
 * 将运行环境与配置映射为口语重练的能力等级。
 *  - level 0: 仅录音+回放，可离线
 *  - level 1: 浏览器 Web Speech STT，可在线
 *  - level 2: Gemini 增强（在线+已配置 Key）
 */
export function detectCapability(opts: CapabilityOptions = {}): CapabilitySnapshot {
  const online = opts.online ?? (typeof navigator !== 'undefined' ? navigator.onLine : true);
  const hasWebSpeech =
    opts.webSpeechAvailable ?? (typeof window !== 'undefined' && !!(window.SpeechRecognition || (window as any).webkitSpeechRecognition));
  // 使用 OpenRouter API Key 检查能力
  const envKey = typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_OPENROUTER_API_KEY : undefined;
  const hasGemini =
    opts.geminiEnabled === false ? false : online && !!(opts.geminiKey ?? envKey);

  let level: CapabilityLevel = 0;
  if (opts.forceLevel !== undefined) {
    level = opts.forceLevel;
  } else if (hasGemini) {
    level = 2;
  } else if (online && hasWebSpeech) {
    level = 1;
  }

  const reasons: string[] = [];
  if (!online) reasons.push('离线，仅提供录音回放');
  if (online && !hasGemini && hasWebSpeech) reasons.push('未配置 Gemini Key，使用浏览器 STT');
  if (!hasWebSpeech) reasons.push('浏览器缺少 Web Speech 接口');
  if (online && !hasGemini) reasons.push('Gemini Key 不可用，未开启增强');
  if (online && hasGemini) reasons.push('已配置 Gemini Key，将尝试 2.5 Flash Audio');

  return {
    level,
    label: labels[level],
    reasons,
    online,
    hasWebSpeech,
    hasGemini,
  };
}
